import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AuthTitleBlock, ConfirmButton, MainLayout, ShouldRender } from 'src/components';
import { PAGES } from 'src/constants/pages';
import { clearAuthToken, signOutUserHelper } from 'src/helpers';
import { signOutUser } from 'src/redux/actions/authActions';
import styles from './ChooseWorkspacePage.module.scss';
import InfoTextBlock from 'src/atoms/InfoTextBlock/InfoTextBlock';
import { TDropdownOption } from './model';
import WorkspaceDropdown from './components/WorkspaceDropdown';
import { getUserWorkspacesHook } from 'src/hooks/getUserWorkspacesHook';
import { setSelectedWorkspace, resetPreselectedAllocationOnWorkspaceChange } from 'src/redux/actions/workspacesActions';
import Loader from 'react-spinners/PuffLoader';
import { useUserValues } from 'src/hooks';
import { getCurrentWorkspaceFromLocalStorage, setCurrentWorkspaceinLocalStorage } from 'src/utils/comon';

const title = {
  choose: 'Choose a workspace',
  empty: 'No workspaces available'
};

const ChooseWorkspacePage = (): JSX.Element => {
  const { push } = useHistory();
  const dispatch = useDispatch();

  const { email } = useUserValues();

  const { userWorkspaces, isLoading } = getUserWorkspacesHook();
  const [isPageLoading, setIsPageLoading] = useState(true);

  useEffect(() => {
    setIsPageLoading(false);
  }, []);

  const [workspace, setWorkspace] = React.useState<TDropdownOption>(() => {
    const savedWorkspase = getCurrentWorkspaceFromLocalStorage(email);

    if (savedWorkspase) {
      return {
        value: savedWorkspase?.workspace?.id ?? '',
        label: savedWorkspase?.workspace?.name ?? 'Select workspace'
      };
    }

    return {
      value: '',
      label: 'Select workspace'
    };
  });

  const options: TDropdownOption[] = useMemo(
    () =>
      userWorkspaces?.map((item) => ({
        value: item.workspace.id,
        label: item.workspace.name
      })),
    [userWorkspaces]
  );

  const isNoWorkspaces = useMemo(() => userWorkspaces?.length === 0, [userWorkspaces]);

  const handleLogOut = async () => {
    try {
      await signOutUserHelper();
      clearAuthToken();
      dispatch(signOutUser());
      push(PAGES.SIGN_IN);
    } catch (error) {
      console.error('handleLogOut error: ', error);
    }
  };

  const onDropdownChange = useCallback((value) => {
    setWorkspace(value);
  }, []);

  const onConfirm = useCallback(() => {
    const selectedWorkspace = userWorkspaces.find((ws) => ws.workspace.id === workspace.value);

    dispatch(setSelectedWorkspace(selectedWorkspace));
    setCurrentWorkspaceinLocalStorage(selectedWorkspace, email);
    dispatch(resetPreselectedAllocationOnWorkspaceChange());
    push(PAGES.DASHBOARD);
  }, [dispatch, email, push, userWorkspaces, workspace.value]);

  const renderContent = useMemo(() => {
    if (isLoading || isPageLoading) {
      return (
        <ShouldRender should={isLoading || isPageLoading}>
          <div className={styles.loaderWrapper}>
            <Loader size={100} color="#077186" />
          </div>
        </ShouldRender>
      );
    }

    if (isNoWorkspaces) {
      return (
        <InfoTextBlock classes={{ wrapper: styles.descriptionWrapper }}>
          Sorry, it seems that you do not have access to any workspaces yet, please reach out to your company
          representative to grant you an access to one.
        </InfoTextBlock>
      );
    }

    return (
      <>
        <WorkspaceDropdown options={options} selectedWorkspace={workspace} onChangeValue={onDropdownChange} />

        <ConfirmButton type="button" style={{ margin: '0 auto' }} onClick={onConfirm} isDisabled={!workspace.value}>
          Continue
        </ConfirmButton>
      </>
    );
  }, [isLoading, isPageLoading, isNoWorkspaces, options, workspace, onDropdownChange, onConfirm]);

  return (
    <MainLayout isCentered isPrivate={false} isFixedHeader>
      <AuthTitleBlock
        title={isLoading ? title.choose : isNoWorkspaces ? title.empty : title.choose}
        button={{ buttonLabel: 'Log Out', onClick: handleLogOut }}
        classes={{
          blockClass: styles.titleBlock,
          linkClass: styles.logOutLink
        }}
      />

      {renderContent}
    </MainLayout>
  );
};

export default ChooseWorkspacePage;
